import { filter } from 'rxjs/operators';
import {
  Component,
  HostListener,
  Input,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { fadeInOutAnimation } from '@toro/common/shared/animations/fadeInOut';
import { DisposableSafelly } from '@toro/common/shared/components/disposable-safelly/disposable-safelly.component';
import { ModalComponent } from '@toro/common/shared/components/modal/modal.component';
import { TrackingActions } from '@toro/common/shared/state-management/tracking/tracking.action';
import { UserProfileState } from '@toro/common/shared/state-management/userprofile/userprofile.reducer';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Observable } from 'rxjs';
import { ILoggedHomeAppState } from '@hub-workspace/apps/logged-home/src/app/shared/interfaces/logged-home-app-state.interface';
import {
  ISTVMDigital,
  ISTVMDigitalPortability,
} from '@hub-workspace/apps/logged-home/src/app/shared/interfaces/stvm-digital.interface';
import { STVMDigitalAction } from '@hub-workspace/apps/logged-home/src/app/state-management/stvm-digital/stvm-digital.action';
import { STVMDigitalState } from '@hub-workspace/apps/logged-home/src/app/state-management/stvm-digital/stvm-digital.reducer';

@AutoUnsubscribe()
@Component({
  selector: 'stvm-digital',
  templateUrl: './stvm-digital.component.html',
  styleUrls: ['./stvm-digital.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [fadeInOutAnimation],
})
export class STVMDigitalComponent extends DisposableSafelly implements OnInit {
  @ViewChild('modalSTVMDigital', { static: false })
  public modalSTVMDigital: ModalComponent;

  @ViewChild('modalAccept', { static: true })
  public modalAccept: ModalComponent;

  @ViewChild('modalDecline', { static: true })
  public modalDecline: ModalComponent;

  @ViewChild('modalError', { static: true })
  public modalError: ModalComponent;

  @Input() public screenOrigin: string;

  public user$: Observable<UserProfileState>;
  public userIdentifier: any;
  public stvmPortabilities: ISTVMDigital[];

  public selectedPortability: ISTVMDigital = null;
  public portabilityAcceptStatus: any;
  public isPending: boolean = false;

  public innerWidth: any;

  constructor(private store: Store<ILoggedHomeAppState>) {
    super();
    this.startObservables();
  }

  private sendToTracking(
    typeAction: string,
    eventsTracking: Record<string, any>
  ) {
    const payloadUser = { type: typeAction, events: eventsTracking };
    this.store.dispatch({
      type: TrackingActions.trackEvents,
      payload: payloadUser,
    });
  }

  private startObservables() {
    this.user$ = this.store.select((state) => state.userProfile);

    // Subscribe que trata as varáveis de cenário
    this.disposableSubscriptions.push(
      this.user$.subscribe((userData) => {
        this.userIdentifier = userData.user.cpfcnpj;
      })
    );

    this.store
      .select((state) => state.stvmPortabilities.pending)
      .subscribe((data) => {
        return (this.isPending = data);
      });

    this.store
      .select((state) => state.stvmPortabilities)
      .pipe(
        filter(
          (stvmPortabilities: STVMDigitalState) => !stvmPortabilities.pending
        )
      )
      .subscribe((stvmDigitalState) => {
        this.stvmPortabilities = stvmDigitalState.portabilities;

        if (stvmDigitalState.showPortabilityAcceptModal) {
          this.openAcceptModal();
        } else if (stvmDigitalState.showPortabilityDeclineModal) {
          this.openDeclineModal();
        } else if (stvmDigitalState.showPortabilityErrorModal) {
          this.openErrorModal();
        }
      });
  }

  ngOnInit(): void {
    this.innerWidth = window.innerWidth;
  }

  @HostListener('window:resize', ['$event'])
  public onResize(event) {
    this.innerWidth = window.innerWidth;
  }

  /**
   *
   * @param action action
   */
  public sendTracking(event: string, action: string, status?: string) {
    this.sendToTracking(event, {
      'Product Area Origin': this.screenOrigin,
      Element: action,
      Status: status,
      Scenario: 'Portabilidade de investimentos',
      'Time Responsável': 'Soho',
      userLogin: this.userIdentifier,
    });
  }

  public openSTVMModal(portability: ISTVMDigital) {
    this.selectedPortability = portability;
    this.modalSTVMDigital.show();
    this.sendTracking(
      'Modal Opened',
      'Carregamento do modal',
      'Aprovar ou desistir'
    );
  }

  public closeSTVMModal() {
    this.modalSTVMDigital.hide();
  }

  public acceptPortability() {
    this.sendTracking(
      'Element Clicked',
      'Aprovar portabilidade',
      'Aprovar ou desistir'
    );

    const payloadPortability: ISTVMDigitalPortability = {
      id: this.selectedPortability.id,
      accepted: true,
    };
    this.store.dispatch({
      type: STVMDigitalAction.acceptPortability,
      payload: payloadPortability,
      portabilities: this.stvmPortabilities,
    });
  }

  public declinePortability() {
    this.sendTracking(
      'Element Clicked',
      'Desistir da portabilidade',
      'Aprovar ou desistir'
    );

    const payloadPortability: ISTVMDigitalPortability = {
      id: this.selectedPortability.id,
      accepted: false,
    };
    this.store.dispatch({
      type: STVMDigitalAction.acceptPortability,
      payload: payloadPortability,
      portabilities: this.stvmPortabilities,
    });
  }

  public openAcceptModal() {
    this.modalSTVMDigital.hide();
    this.modalAccept.show();
    this.sendTracking(
      'Modal Opened',
      'Carregamento do modal de aprovação enviada',
      'Aprovação enviada'
    );
  }

  public closeAcceptModal() {
    this.modalAccept.hide();
    this.sendTracking('Element Clicked', 'Entendi', 'Aprovação enviada');
  }

  public openDeclineModal() {
    this.modalSTVMDigital.hide();
    this.modalDecline.show();
    this.sendTracking(
      'Modal Opened',
      'Carregamento do modal de desistência enviada',
      'Desistência enviada'
    );
  }

  public closeDeclineModal() {
    this.modalDecline.hide();
    this.sendTracking('Element Clicked', 'Entendi', 'Desistência enviada');
  }

  public openErrorModal() {
    this.modalSTVMDigital.hide();
    this.modalAccept.hide();
    this.modalDecline.hide();

    this.modalError.show();
    this.sendTracking(
      'Modal Opened',
      'Carregamento do modal de erro genérico',
      'Erro genérico'
    );
  }

  public closeErrorModal() {
    this.modalError.hide();
    this.sendTracking('Element Clicked', 'Entendi', 'Erro genérico');
  }
}
